import { useEffect } from 'react';
import Footer from '../../home/components/Footer';

export default function KitchenRemodelingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://americandreamremodelinc.com';

    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Kitchen Remodeling',
      description: 'Custom kitchen remodeling in Southeast Michigan — cabinetry, countertops, layouts, lighting, backsplash, and appliance installation by American Dream Remodeling INC.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'American Dream Remodeling INC',
        url: siteUrl,
        telephone: '+1-586-443-0294',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Detroit',
          addressRegion: 'MI',
          addressCountry: 'US',
        },
      },
      areaServed: [
        { '@type': 'County', name: 'Macomb County' },
        { '@type': 'County', name: 'Oakland County' },
        { '@type': 'County', name: 'Wayne County' },
        { '@type': 'County', name: 'Washtenaw County' },
      ],
      url: `${siteUrl}/services/kitchen-remodeling`,
    };

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/#services` },
        { '@type': 'ListItem', position: 3, name: 'Kitchen Remodeling', item: `${siteUrl}/services/kitchen-remodeling` },
      ],
    };

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How much does kitchen remodeling cost in Southeast Michigan?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Kitchen remodeling costs vary based on scope, materials, and layout changes. American Dream Remodeling INC offers free, no-obligation estimates for all kitchen remodeling projects in Macomb, Oakland, Wayne, and Washtenaw County.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long does a kitchen remodel take?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A typical kitchen remodel takes 3–6 weeks depending on the scope of work. Full gut renovations with layout changes may take longer. We provide a detailed timeline during your free consultation.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do you handle permits for kitchen remodeling in Michigan?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, American Dream Remodeling INC handles all necessary permits for kitchen remodeling projects throughout Southeast Michigan, including structural, electrical, and plumbing permits.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can you remodel a kitchen while we live in the home?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, we work efficiently to minimize disruption. We plan the project in phases where possible and keep the work area contained so your family can continue living comfortably during the kitchen remodel.',
          },
        },
      ],
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Kitchen Remodeling in Southeast Michigan | American Dream Remodeling INC',
      description: 'Custom kitchen remodeling in Southeast Michigan — cabinetry, countertops, layouts, lighting, backsplash, and appliance installation by American Dream Remodeling INC.',
      url: `${siteUrl}/services/kitchen-remodeling`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/#services` },
          { '@type': 'ListItem', position: 3, name: 'Kitchen Remodeling', item: `${siteUrl}/services/kitchen-remodeling` },
        ],
      },
    };

    const injectSchema = (id: string, data: object) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        (el as HTMLScriptElement).type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };

    injectSchema('schema-kitchen-service', serviceSchema);
    injectSchema('schema-kitchen-breadcrumb', breadcrumb);
    injectSchema('schema-kitchen-faq', faqSchema);
    injectSchema('schema-kitchen-webpage', webPageSchema);

    return () => {
      ['schema-kitchen-service', 'schema-kitchen-breadcrumb', 'schema-kitchen-faq', 'schema-kitchen-webpage'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  const scrollToEstimate = () => {
    sessionStorage.setItem('scrollToEstimate', 'true');
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/c6a5723c197a02e9ed144834f77fad16.jpg"
            alt="Kitchen Remodeling Services"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Kitchen Remodeling</h1>
          <p className="text-xl md:text-2xl mb-8">Transform your kitchen into a modern, functional space with custom cabinetry, countertops, and layouts designed for your lifestyle.</p>
          <button
            onClick={scrollToEstimate}
            className="bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-600 transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Get Free Estimate
          </button>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Remodel Your Kitchen?</h2>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Your kitchen is the heart of your home. Whether you're looking to increase functionality, update outdated features, or create a space that reflects your personal style, a kitchen remodel can dramatically improve your daily life and add significant value to your home.
              </p>
              <p className="text-gray-600 leading-relaxed">
                At American Dream Remodeling INC, we specialize in creating custom kitchens that blend beauty with practicality. From layout redesign to custom cabinetry and premium finishes, we handle every detail with precision and care.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=elegant%20kitchen%20island%20with%20marble%20countertop%20bar%20stools%20modern%20pendant%20lights%20white%20cabinets%20clean%20simple%20background%20professional%20interior%20photography&width=800&height=600&seq=kitchen-detail-001&orientation=landscape"
                alt="Custom Kitchen Island"
                className="w-full h-full object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>

          {/* What We Offer */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What We Offer</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: 'ri-layout-grid-line', title: 'Custom Layouts', desc: 'Optimize your kitchen flow with smart design' },
                { icon: 'ri-inbox-line', title: 'Custom Cabinetry', desc: 'Beautiful, functional storage solutions' },
                { icon: 'ri-contrast-drop-2-line', title: 'Countertops', desc: 'Granite, quartz, marble, and more' },
                { icon: 'ri-lightbulb-line', title: 'Lighting Design', desc: 'Task, ambient, and accent lighting' },
                { icon: 'ri-stack-line', title: 'Backsplash', desc: 'Stunning tile and stone installations' },
                { icon: 'ri-tools-line', title: 'Appliance Installation', desc: 'Seamless integration of modern appliances' },
              ].map((item, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-500 text-white rounded-lg mb-4">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Process */}
          <div className="bg-gradient-to-br from-gray-50 to-white p-12 rounded-2xl border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: '1', title: 'Consultation', desc: 'We discuss your vision, needs, and budget' },
                { step: '2', title: 'Design', desc: 'Custom plans and 3D renderings' },
                { step: '3', title: 'Construction', desc: 'Expert installation and craftsmanship' },
                { step: '4', title: 'Completion', desc: 'Final walkthrough and your dream kitchen' },
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-teal-500 text-white rounded-full text-2xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Transform Your Kitchen?</h2>
          <p className="text-xl text-white/90 mb-8">Get a free, no-obligation estimate for your kitchen remodeling project in Southeast Michigan.</p>
          <button
            onClick={scrollToEstimate}
            className="bg-white text-teal-600 px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Get Your Free Estimate
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}